import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { ARTIST_NAME, IMG, CONTACT, SOCIALS } from "@/data/content";
import { Copy } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal, ChapterLabel, MaskLines, ParallaxImage, usePageTitle } from "@/components/Shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-white/10 bg-[#141416] px-5 py-4 font-sans text-sm text-[#F9F8F6] placeholder:text-[#71717A] outline-none transition-colors duration-300 focus:border-[#C5A059]/60";
const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]";

const ContactPage = () => {
  const { t, lang, lf, lp } = useLang();
  usePageTitle(`${t.nav.contact} — ${ARTIST_NAME}`);

  const [category, setCategory] = useState(() => {
    const c = new URLSearchParams(window.location.search).get("category");
    return ["booking", "events", "press", "lessons", "workshop", "general"].includes(c) ? c : "booking";
  });
  const [form, setForm] = useState({ name: "", email: "", company: "", subject: "", event_type: "", date: "", location: "", guests: "", message: "", website: "" });
  const [sending, setSending] = useState(false);
  const [startedAt] = useState(() => Date.now());

  const isEvent = category === "booking" || category === "events";
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT.email);
      toast.success(t.contact.copied);
    } catch (_) {
      toast.error(t.contact.error);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.website) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, category, lang, started_at: startedAt });
      toast.success(t.contact.success);
      setForm({ name: "", email: "", company: "", subject: "", event_type: "", date: "", location: "", guests: "", message: "", website: "" });
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="relative">
      {/* Background portrait */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[110vh] overflow-hidden">
        <ParallaxImage src={IMG.contact} alt="" className="h-full" imgClass="opacity-30 object-[65%_40%]" speed={30} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0C]/80 via-[#0B0B0C]/30 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0C]/30 via-[#0B0B0C]/60 to-[#0B0B0C]" />
      </div>

      <div className="relative z-10 px-6 pb-24 pt-40 sm:px-12 sm:pt-48 lg:px-20">
        <Reveal immediate duration={2} y={20}>
          <p className="mb-6 font-sans text-xs font-medium uppercase tracking-[0.35em] text-gold">{t.contact.eyebrow}</p>
        </Reveal>
        <MaskLines as="h1" text={t.contact.title} className="font-serif text-6xl font-light leading-[0.9] text-[#F9F8F6] sm:text-8xl lg:text-9xl" />
        <Reveal immediate duration={2} delay={0.4}>
          <p className="mt-8 max-w-xl font-sans text-sm leading-relaxed text-[#A1A1AA] sm:text-base">{t.contact.subtitle}</p>
        </Reveal>

        <div className="mt-20 grid gap-16 lg:grid-cols-12">
          {/* Form */}
          <form data-testid="contact-form" onSubmit={submit} className="lg:col-span-7">
            {/* Category */}
            <Reveal immediate duration={2}>
              <div className="mb-10 flex flex-wrap gap-3">
                {Object.entries(t.contact.categories).map(([key, label]) => (
                  <button
                    type="button"
                    key={key}
                    data-testid={`contact-category-${key}`}
                    onClick={() => setCategory(key)}
                    className={`border px-6 py-2.5 font-sans text-[10px] font-medium uppercase tracking-[0.3em] transition-all duration-400 ${
                      category === key
                        ? "border-[#C5A059] bg-[#C5A059] text-[#0B0B0C]"
                        : "border-white/15 text-[#A1A1AA] hover:border-[#C5A059]/50 hover:text-[#C5A059]"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </Reveal>

            <div className="grid gap-6 sm:grid-cols-2">
              <Reveal immediate duration={2} delay={0.05}>
                <label className={labelCls}>{t.contact.form.name}</label>
                <input required autoComplete="name" maxLength={120} data-testid="booking-form-name-input" value={form.name} onChange={set("name")} className={inputCls} />
              </Reveal>
              <Reveal immediate duration={2} delay={0.08}>
                <label className={labelCls}>{t.contact.form.email}</label>
                <input required type="email" autoComplete="email" maxLength={200} data-testid="booking-form-email-input" value={form.email} onChange={set("email")} className={inputCls} />
              </Reveal>
              <Reveal immediate duration={2} delay={0.11}>
                <label className={labelCls}>{t.contact.form.company}</label>
                <input data-testid="contact-form-company-input" value={form.company} onChange={set("company")} className={inputCls} />
              </Reveal>
              <Reveal immediate duration={2} delay={0.14}>
                <label className={labelCls}>{t.contact.form.subject}</label>
                <input data-testid="contact-form-subject-input" value={form.subject} onChange={set("subject")} className={inputCls} />
              </Reveal>

              {isEvent && (
                <>
                  <Reveal immediate duration={2} delay={0.17}>
                    <label className={labelCls}>{t.contact.form.eventType}</label>
                    <select
                      data-testid="booking-form-event-type-select"
                      value={form.event_type}
                      onChange={set("event_type")}
                      className={`${inputCls} appearance-none`}
                    >
                      <option value="">—</option>
                      {t.contact.form.eventTypes.map((et) => (
                        <option key={et} value={et}>{et}</option>
                      ))}
                    </select>
                  </Reveal>
                  <Reveal immediate duration={2} delay={0.2}>
                    <label className={labelCls}>{t.contact.form.date}</label>
                    <input type="date" data-testid="contact-form-date-input" value={form.date} onChange={set("date")} className={`${inputCls} [color-scheme:dark]`} />
                  </Reveal>
                  <Reveal immediate duration={2} delay={0.23}>
                    <label className={labelCls}>{t.contact.form.location}</label>
                    <input data-testid="contact-form-location-input" value={form.location} onChange={set("location")} className={inputCls} />
                  </Reveal>
                  <Reveal immediate duration={2} delay={0.26}>
                    <label className={labelCls}>{t.contact.form.guests}</label>
                    <input data-testid="contact-form-guests-input" value={form.guests} onChange={set("guests")} className={inputCls} />
                  </Reveal>
                </>
              )}
            </div>

            <Reveal immediate duration={2} delay={0.3} className="mt-6">
              <label className={labelCls}>{t.contact.form.message}</label>
              <textarea required rows={6} minLength={10} maxLength={5000} data-testid="contact-form-message-input" value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} />
            </Reveal>

            <Reveal immediate duration={2} delay={0.34} className="mt-8">
              <p data-testid="contact-form-privacy-notice" className="max-w-xl font-sans text-xs leading-relaxed text-[#71717A]">
                {t.contact.form.notice}{" "}
                <Link to={lp("/datenschutz")} data-testid="contact-form-privacy-link" className="underline underline-offset-2 transition-colors hover:text-[#C5A059]">{t.contact.form.noticeLink}</Link>.
              </p>
              <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={set("website")} className="absolute left-[-9999px] h-0 w-0 opacity-0" />
            </Reveal>

            <Reveal immediate duration={2} delay={0.38} className="mt-10">
              <button
                type="submit"
                disabled={sending}
                data-testid="booking-form-submit-button"
                data-cursor="OPEN"
                className="inline-flex items-center gap-4 bg-[#C5A059] px-12 py-4 font-sans text-[11px] font-medium uppercase tracking-[0.3em] text-[#0B0B0C] transition-all duration-400 hover:bg-[#E5C158] disabled:cursor-not-allowed disabled:opacity-40"
              >
                {sending ? t.contact.form.sending : t.contact.form.submit}
              </button>
            </Reveal>
          </form>

          {/* Direct contact */}
          <div className="lg:col-span-4 lg:col-start-9">
            <Reveal immediate duration={2} delay={0.2}>
              <ChapterLabel label={t.contact.directLabel} />
              <div className="space-y-8">
                <div data-testid="contact-address">
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">{t.contact.addressLabel}</p>
                  <p className="mt-2 font-sans text-base font-medium tracking-wide text-[#F9F8F6]">{CONTACT.name}</p>
                  <p className="font-sans text-xs text-[#71717A]">{lf(CONTACT.title)}</p>
                  <p className="mt-3 font-sans text-sm leading-relaxed text-[#A1A1AA]">
                    {CONTACT.street}<br />{CONTACT.city}<br />{lf(CONTACT.country)}
                  </p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">{t.contact.phoneLabel}</p>
                  <a href={CONTACT.phoneHref} data-testid="contact-phone-link" data-cursor="CALL" className="mt-2 block font-sans text-base text-[#F9F8F6] transition-colors hover:text-[#C5A059]">
                    {CONTACT.phone}
                  </a>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">{t.contact.emailLabel}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <span data-testid="contact-email-text" className="font-sans text-base text-[#F9F8F6]">{CONTACT.email}</span>
                    <button
                      type="button"
                      data-testid="contact-email-copy-button"
                      onClick={copyEmail}
                      className="inline-flex items-center gap-1.5 border border-white/15 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.25em] text-[#A1A1AA] transition-colors hover:border-[#C5A059] hover:text-[#C5A059]"
                    >
                      <Copy className="h-3 w-3" /> {t.contact.copy}
                    </button>
                  </div>
                  <p className="mt-2 font-sans text-xs text-[#71717A]">{t.contact.directHint}</p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">{t.contact.socialLabel}</p>
                  <div className="mt-3 flex flex-col gap-2">
                    {SOCIALS.map((s) => (
                      <a key={s.label} href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`contact-social-${s.label.toLowerCase()}`} className="font-sans text-sm text-[#A1A1AA] transition-colors hover:text-[#C5A059]">
                        {s.label} ↗
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
